import { AccountsCell } from "@/components/shared/cells/accounts";
import type { InvoiceRowData } from "@/components/shared/data-table";
import type { FormValues as AccountFormValues } from "@/components/shared/form/lineItems/account";
import { Button } from "@/components/ui/button";
import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import { confColor } from "@/lib/miscellaneous/confidence-color";
import { cn } from "@/lib/utils";
import { useTenant } from "@/providers/session";
import { useGetAllInvoices } from "@/queries/invoices/get-all";
import { useUpdateInvoice } from "@/queries/invoices/update";
import { useUpdateManyLineItems } from "@/queries/line-items/update";
import { useNavigate } from "@tanstack/react-router";
import type { CellContext } from "@tanstack/react-table";
import { useCallback } from "react";

type Props = CellContext<InvoiceRowData, unknown> & {
  showConfidence?: boolean;
  readOnly?: boolean;
};

export const ExpenseAccountCell = ({
  row,
  showConfidence = false,
  readOnly = false,
}: Props) => {
  const { fetchInvoices } = useGetAllInvoices({
    enabled: false,
    skipDbClear: true,
  });
  const navigate = useNavigate();
  const { companySlug } = useTenant();
  const [updateInvoice] = useUpdateInvoice();
  const updateLineItems = useUpdateManyLineItems({
    disableOptimisticResponse: true,
  });

  const handleRefreshRow = useCallback(
    async (values: AccountFormValues) => {
      await updateLineItems(
        row.original.lineItems.map((lineItem) => lineItem.lineItemId),
        {
          accountId: values.accountId,
        },
      );
      fetchInvoices({
        query: {
          invoiceId: {
            value: [row.original.invoice.invoiceId],
            op: "in",
          },
        },
      });
    },
    [row.original.invoice.invoiceId, fetchInvoices, updateInvoice],
  );

  const handleManualProcessing = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click event

    if (row.original.invoice.invoiceId) {
      // Set manualProcessing to true and update status via GraphQL mutation
      updateInvoice({
        invoiceId: row.original.invoice.invoiceId,
        manualProcessing: true,
        status: InvoiceStatus.PREPROCESSED,
      });

      // Navigate to the invoice review page
      navigate({
        to: "/$company/buchhaltung/rechnung/$id",
        params: { company: companySlug, id: row.original.invoice.invoiceId },
      });
    }
  };

  if (
    row.original.invoice.status === InvoiceStatus.NEW &&
    row.original.invoice?.processingStatus !== "UPLOADED"
  ) {
    return (
      <div className="flex items-center justify-start invisible group-hover:visible">
        <Button
          variant="outline"
          size="sm"
          onClick={handleManualProcessing}
          className="text-xs "
        >
          Ohne KI Bearbeiten
        </Button>
      </div>
    );
  } else if (
    row.original.invoice.status === InvoiceStatus.NEW ||
    row.original.invoice.status === InvoiceStatus.ERROR ||
    row.original.invoice.splitPdf === true ||
    !row.original.invoice.isBooking
  ) {
    return null;
  }

  const accountIds = row.original.invoice.accountIds;
  const accountsConfidence = row.original.invoice.accountConfidence;
  const lineItemsIds = row.original.lineItems.map(
    (lineItem) => lineItem.lineItemId,
  );

  return (
    <AccountsCell
      lineItemIds={lineItemsIds}
      accountIds={accountIds}
      onAccountChange={handleRefreshRow}
      className={cn(confColor(showConfidence ? accountsConfidence : undefined))}
      readOnly={readOnly}
    />
  );
};
